import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { EventType } from 'matrix-js-sdk/lib/@types/event';
import { Direction } from 'matrix-js-sdk/lib/models/event-timeline';
import sdk from '../../libs/mapp/sdk';
import mcli from '../../libs/matrix';
import { getSanitizedMDirectContent } from '../../libs/matrix/dmAccountData';
import { getNestedReplyText } from '../../libs/matrix/reply';
import { isAdminInvite } from '../../libs/matrix/invite';
import { isInviteDeclined } from '../../libs/matrix/declinedInvites';
import { addPendingInvite } from '../app';
const ROOM_STATUS_EVENTS = new Set(['m.room.member', 'm.room.name', 'm.room.topic']);
const paginate = (room, nr, direction) => {
    return new Promise((resolve, reject) => {
        const liveTimeline = room?.getLiveTimeline();
        if (!liveTimeline)
            return;
        mcli
            .paginateEventTimeline(liveTimeline, {
            backwards: direction === Direction.Backward,
            limit: nr,
        })
            .catch(reject)
            .then((event) => {
            resolve(event);
        });
    });
};
const isDirect = (roomId) => {
    const dmRoomMap = getSanitizedMDirectContent();
    return Object.values(dmRoomMap).some((roomIds) => roomIds.includes(roomId));
};
const isSystemMember = (userId, name) => {
    const normalizedUserId = (userId || '').toLowerCase();
    const normalizedName = (name || '').toLowerCase();
    return normalizedName === 'matrixadmin' || normalizedUserId.includes('matrixadmin');
};
const getDirectPeerDisplayName = (room) => {
    const myUserId = mcli.getUserId();
    const members = room.getMembers();
    const peer = members.find((member) => member.userId !== myUserId &&
        member.membership !== 'leave' &&
        member.membership !== 'ban' &&
        !isSystemMember(member.userId, member.name));
    if (!peer) {
        return '';
    }
    // Try multiple sources for the display name
    // 1. RoomMember.name (SDK resolved)
    // 2. RoomMember.rawDisplayName
    // 3. Get displayname from the m.room.member state event directly
    // 4. Matrix User profile from SDK cache
    // 5. Fallback to userId
    let displayName = peer.name || peer.rawDisplayName;
    if (!displayName || displayName === peer.userId || displayName.includes('@')) {
        // Try to get displayname from state event
        const memberEvent = room.currentState.getStateEvents('m.room.member', peer.userId);
        const eventDisplayName = memberEvent?.getContent()?.displayname;
        if (eventDisplayName) {
            displayName = eventDisplayName;
        }
    }
    if (!displayName || displayName === peer.userId || displayName.includes('@')) {
        // Try SDK User cache
        const matrixUser = mcli.getUser(peer.userId);
        if (matrixUser?.displayName) {
            displayName = matrixUser.displayName;
        }
    }
    // Final fallback
    if (!displayName) {
        displayName = peer.userId;
    }
    return displayName;
};
const upsertDirectRoom = async (roomId, userId) => {
    if (!userId)
        return;
    const dmRoomMap = {
        ...getSanitizedMDirectContent(),
    };
    Object.keys(dmRoomMap).forEach((id) => {
        const roomList = dmRoomMap[id] || [];
        if (id !== userId) {
            dmRoomMap[id] = roomList.filter((mappedRoomId) => mappedRoomId !== roomId);
        }
    });
    const userRooms = dmRoomMap[userId] || [];
    if (!userRooms.includes(roomId)) {
        dmRoomMap[userId] = [...userRooms, roomId];
    }
    await mcli.setAccountData(EventType.Direct, dmRoomMap);
};
const MESSAGE_EVENT_TYPES = new Set(['m.room.message', 'm.room.encrypted']);
const MIN_MESSAGE_EVENTS = 25;
const ensureMinimumMessageEvents = async (timeline, initialTimelineEvents, _events, skipPagination) => {
    const countTimelineMessageEvents = (evts) => evts.filter((e) => MESSAGE_EVENT_TYPES.has(e.getType())).length;
    const MAX_PAGINATION_ATTEMPTS = 5;
    let timelineEvents = initialTimelineEvents;
    let messageCount = countTimelineMessageEvents(timelineEvents);
    let canPaginate = !skipPagination;
    let attempts = 0;
    while (messageCount < MIN_MESSAGE_EVENTS && canPaginate && attempts < MAX_PAGINATION_ATTEMPTS) {
        attempts += 1;
        const prevTimelineLength = timelineEvents.length;
        try {
            const paginated = await mcli.paginateEventTimeline(timeline, {
                backwards: true,
                limit: 20,
            });
            if (paginated) {
                timelineEvents = timeline.getEvents();
            }
            else {
                canPaginate = false;
            }
        }
        catch {
            canPaginate = false;
        }
        messageCount = countTimelineMessageEvents(timelineEvents);
        // Stop when no progress is possible (start of room timeline reached or server returned no new data).
        if (timelineEvents.length <= prevTimelineLength) {
            canPaginate = false;
        }
    }
    return timelineEvents;
};
const resolveActiveMembers = (room) => {
    const members = [];
    room.getMembers().forEach((m) => {
        if (m.membership === 'leave' || m.membership === 'ban')
            return;
        members.push({
            userId: m.userId,
            imageUrl: m.getAvatarUrl(mcli.baseUrl, 49, 49, 'crop', true, true) || '',
            name: m.name,
            membership: m.membership || '',
            typing: false,
            roomId: m.roomId,
            presence: m.user?.presence || '',
            powerLevel: m.powerLevel,
        });
    });
    return members;
};
const mapTimelineEventToRoomEvent = async (room, timelineEvent, events, resolvedMembers) => {
    const currentContent = timelineEvent.getContent();
    const shouldAttemptDecrypt = timelineEvent.isEncrypted() || currentContent?.msgtype === 'm.bad.encrypted';
    if (shouldAttemptDecrypt) {
        try {
            await mcli.decryptEventIfNeeded(timelineEvent);
        }
        catch {
            // Keep rendering placeholder content if decryption still fails for now.
            console.error('Failed to decrypt event', timelineEvent.getId());
        }
    }
    const remoteEventId = timelineEvent?.getId();
    const txnId = timelineEvent?.getTxnId?.();
    const id = remoteEventId || (txnId ? `~local:${txnId}` : null);
    if (typeof id !== 'string')
        return null;
    if (timelineEvent.getType() === 'm.reaction')
        return null;
    const existingIdx = events.findIndex((ev) => ev.id === id || (txnId && ev.txnId && ev.txnId === txnId));
    if (existingIdx >= 0) {
        const existing = events[existingIdx];
        const sameEventId = existing.id === id;
        const sameSendingState = existing.isSending === timelineEvent.isSending();
        const sameContent = JSON.stringify(existing.content || {}) === JSON.stringify(timelineEvent.getContent() || {});
        const samePrevContent = JSON.stringify(existing.prevContent || {}) ===
            JSON.stringify(timelineEvent.getPrevContent() || {});
        const sameEncryptionState = existing.isEncrypted === timelineEvent.isEncrypted();
        const sameRedactionState = existing.isRedacted === timelineEvent.isRedacted();
        if (sameEventId &&
            sameSendingState &&
            sameContent &&
            samePrevContent &&
            sameEncryptionState &&
            sameRedactionState) {
            return null;
        }
        // Replace local echo / stale copy with fresher timeline event.
        events.splice(existingIdx, 1);
    }
    const users = room.getUsersReadUpTo(timelineEvent);
    const haveRead = resolvedMembers.filter((member) => {
        const lastRead = room.getReadReceiptForUserId(member.userId);
        return Boolean(lastRead && lastRead?.data.ts > timelineEvent.getTs());
    });
    const readState = resolvedMembers.length === haveRead.length ? 2 : 1;
    const readEventId = room.getEventReadUpTo(mcli.getUserId() || '');
    return {
        id,
        txnId,
        type: timelineEvent.getType(),
        content: timelineEvent.getContent(),
        prevContent: timelineEvent.getPrevContent(),
        sender: timelineEvent?.getSender() || '',
        timestamp: timelineEvent.localTimestamp,
        roomId: timelineEvent.getRoomId() || '',
        replyTo: timelineEvent.replyEventId || null,
        readState,
        haveRead,
        isStatusEvent: ROOM_STATUS_EVENTS.has(timelineEvent.getType()),
        isSending: id.startsWith('$') ? false : timelineEvent.isSending(),
        isEncrypted: timelineEvent.isEncrypted(),
        isRedacted: timelineEvent.isRedacted(),
        lastEventRead: readEventId || '',
        threadRootId: timelineEvent.threadRootId,
        relation: timelineEvent.getRelation(),
        replyEvent: getNestedReplyText(timelineEvent),
        usersRead: haveRead,
        isReadByMe: users.includes(mcli.getUserId() || ''),
    };
};
const getMatrixState = (state) => {
    if (state.matrix) {
        return state.matrix;
    }
    return state;
};
const getRoom = async (state, room, skipPagination = false) => {
    const matrixState = getMatrixState(state);
    const timeline = room.getLiveTimeline();
    let lastMessage = '';
    let lastEventId = '';
    const existingRoom = [...matrixState.rooms, ...matrixState.archivedRooms].find((r) => r.id === room.roomId);
    const events = existingRoom?.events ? [...existingRoom.events] : [];
    let timelineEvents = timeline.getEvents();
    const resolvedMembers = resolveActiveMembers(room);
    const directRoom = isDirect(room.roomId);
    let isEncrypted = false;
    // Paginate backwards until we have enough message events unless this is the startup bulk load.
    timelineEvents = await ensureMinimumMessageEvents(timeline, timelineEvents, events, skipPagination);
    for (const timeLineEvent of timelineEvents) {
        if (timeLineEvent.getType() === 'm.room.encryption' || timeLineEvent.isEncrypted())
            isEncrypted = true;
        const mappedEvent = await mapTimelineEventToRoomEvent(room, timeLineEvent, events, resolvedMembers);
        if (mappedEvent) {
            events.push(mappedEvent);
        }
        if (events.length > 0) {
            const last = timelineEvents.at(-1);
            lastMessage = last?.getContent().body || '';
            lastEventId = last?.getId() || '';
        }
    }
    let avatar = room.getAvatarUrl(mcli.baseUrl, 80, 80, 'crop');
    if (!avatar && resolvedMembers.length === 2) {
        resolvedMembers.forEach((member) => {
            if (member.userId !== mcli.getUserId()) {
                avatar = member.imageUrl;
            }
        });
    }
    // Sort events chronologically by timestamp to ensure date dividers are in correct order
    events.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    // Check if room name looks like a Matrix default placeholder
    const rawName = room?.name || '';
    const looksLikeEmptyRoom = !rawName || rawName.toLowerCase() === 'empty room' || rawName.startsWith('!');
    // For DMs (or 2-person rooms that look like DMs), resolve peer display name
    const shouldResolvePeerName = looksLikeEmptyRoom && (directRoom || resolvedMembers.length === 2);
    console.log('[getRoom] Room:', room.roomId, 'rawName:', rawName, 'looksLikeEmptyRoom:', looksLikeEmptyRoom, 'directRoom:', directRoom, 'resolvedMembers.length:', resolvedMembers.length, 'shouldResolvePeerName:', shouldResolvePeerName);
    const resolvedName = shouldResolvePeerName ? getDirectPeerDisplayName(room) || rawName : rawName;
    console.log('[getRoom] Final resolvedName:', resolvedName);
    return {
        id: room.roomId,
        name: resolvedName || '',
        lastMessage,
        lastEventId,
        pin: false,
        mute: false,
        imageUrl: avatar || '',
        events,
        members: resolvedMembers.filter((m) => {
            if (isSystemMember(m.userId, m.name))
                return false;
            if (!directRoom && m.name.includes('@'))
                return false;
            return true;
        }),
        isPublicGroup: room.getJoinRule() === 'public' || !resolvedMembers.some((m) => m.name === 'matrixadmin'),
        userTyping: [],
        isEncrypted,
        isDirect: directRoom,
    };
};
export const getRoomsAsync = createAsyncThunk('matrix/getRoomsAsync', async (_payload, thunkAPI) => {
    const rooms = mcli.getRooms();
    const eventRoom = {};
    let manualInviteEnabled = false;
    try {
        const config = await sdk.Configuration.get();
        manualInviteEnabled = config?.chatAutoJoin === false;
    }
    catch {
        manualInviteEnabled = false;
    }
    // Handle invite rooms first - sequential because upsertDirectRoom writes account data
    for (const room of rooms) {
        if (room.getMyMembership() !== 'invite')
            continue;
        if (manualInviteEnabled && !isAdminInvite(room)) {
            if (isInviteDeclined(room.roomId)) {
                mcli.leave(room.roomId).catch(() => undefined);
                continue;
            }
            thunkAPI.dispatch(addPendingInvite(room.roomId));
            continue;
        }
        try {
            const invitedBy = room.getDMInviter();
            const joinedRoom = await mcli.joinRoom(room.roomId);
            if (invitedBy) {
                await upsertDirectRoom(joinedRoom.roomId, invitedBy).catch((e) => {
                    console.warn('Failed to update m.direct after invite join', e);
                });
            }
        }
        catch (e) {
            console.error('Error joining invited room:', room.roomId, e);
        }
    }
    // Filter rooms to process in parallel
    const roomsToProcess = rooms.filter((room) => {
        const membership = room.getMyMembership();
        if (membership === 'ban' || membership === 'invite')
            return false;
        return true;
    });
    console.log(`Rooms to process: ${roomsToProcess.map((r) => r.roomId).join(', ')}`);
    // Process all rooms in parallel — getRoom() only reads Matrix in-memory state
    const results = await Promise.all(roomsToProcess.map(async (room) => {
        try {
            return (await getRoom(thunkAPI.getState(), room, true));
        }
        catch (e) {
            console.error('Error while handling room:', room.roomId, room.name, e);
            return null;
        }
    }));
    const resolved = [];
    const archivedRooms = [];
    results.forEach((resolvedRoom, index) => {
        if (!resolvedRoom)
            return;
        const room = roomsToProcess[index];
        const myMembership = room?.getMyMembership();
        // Completely hide rooms where I have left
        if (myMembership === 'leave') {
            return;
        }
        // Check if this is a direct chat where the other user left
        if (resolvedRoom.isDirect) {
            const members = room?.getMembers() || [];
            const myUserId = mcli.getUserId();
            const peerMembers = members.filter((m) => m.userId !== myUserId && !isSystemMember(m.userId, m.name));
            const hasActivePeer = peerMembers.some((m) => m.membership !== 'leave' && m.membership !== 'ban');
            // Archive only when all real peers are gone.
            if (peerMembers.length > 0 && !hasActivePeer) {
                archivedRooms.push(resolvedRoom);
                return;
            }
        }
        // All other rooms go to active
        resolved.push(resolvedRoom);
    });
    [...resolved, ...archivedRooms].forEach((r) => {
        r.events.forEach((ev) => {
            eventRoom[ev.id] = ev.roomId;
        });
    });
    return { rooms: resolved, archivedRooms, eventRoom };
});
export const getUsersAsync = createAsyncThunk('matrix/getUsersAsync', async (payload) => {
    const mappUsers = await sdk.ChatUsers.get({
        searchTerm: payload?.searchTerm || '',
        page: payload?.page?.toString() || '1',
    });
    const resolved = [];
    mappUsers.items.forEach((user) => {
        if (user.name === 'matrixadmin' ||
            user.name?.includes('@') ||
            user.matrixId === mcli.getUserId())
            return;
        const matrixUser = mcli.getUser(user.matrixId);
        resolved.push({
            userId: user.matrixId,
            imageUrl: mcli.mxcUrlToHttp(matrixUser?.avatarUrl || '') || '',
            name: user.firstName + ' ' + user.lastName || '',
            membership: '',
            typing: false,
            roomId: '',
            presence: matrixUser?.presence || '',
        });
    });
    return { users: { items: resolved, pagination: mappUsers.pagination } };
});
export const getPublicRooms = createAsyncThunk('matrix/getPublicRooms', async (thunkAPI) => {
    const rooms = await mcli.publicRooms();
    const publicRooms = [];
    rooms.chunk.forEach((room) => {
        publicRooms.push(room);
    });
    return { publicRooms };
});
export const getRoomAsync = createAsyncThunk('matrix/getRoomAsync', async (id, thunkAPI) => {
    const room = mcli.getRoom(id);
    if (!room)
        return;
    return { room: await getRoom(thunkAPI.getState(), room) };
});
export const paginateRoomAsync = createAsyncThunk('matrix/paginateRoomAsync', async (payload, thunkAPI) => {
    const room = mcli.getRoom(payload.roomId);
    if (!room)
        return;
    const isPaginated = await paginate(room, 20, Direction.Backward);
    if (!isPaginated)
        return;
    return { room: await getRoom(thunkAPI.getState(), room) };
});
export const jumpToRoomEventAsync = createAsyncThunk('matrix/jumpToRoomEventAsync', async (payload, thunkAPI) => {
    const room = mcli.getRoom(payload.roomId);
    if (!room)
        return;
    const matrixState = getMatrixState(thunkAPI.getState());
    const existingRoom = [...matrixState.rooms, ...matrixState.archivedRooms].find((entry) => entry.id === payload.roomId);
    const existingEvents = existingRoom?.events ? [...existingRoom.events] : [];
    let targetTimelineEvent = room.findEventById(payload.eventId);
    let contextTimeline = null;
    if (!targetTimelineEvent) {
        const timeline = await mcli.getEventTimeline(room.getUnfilteredTimelineSet(), payload.eventId);
        contextTimeline = timeline;
        targetTimelineEvent =
            room.findEventById(payload.eventId) ||
                timeline?.getEvents().find((event) => event.getId() === payload.eventId);
    }
    if (!targetTimelineEvent) {
        return { roomId: payload.roomId, eventId: payload.eventId, events: existingEvents };
    }
    const timelineEvents = contextTimeline?.getEvents() || [targetTimelineEvent];
    const resolvedMembers = resolveActiveMembers(room);
    const hydratedEvents = [...existingEvents];
    for (const timelineEvent of timelineEvents) {
        const mappedEvent = await mapTimelineEventToRoomEvent(room, timelineEvent, hydratedEvents, resolvedMembers);
        if (mappedEvent) {
            hydratedEvents.push(mappedEvent);
        }
    }
    return {
        roomId: payload.roomId,
        eventId: payload.eventId,
        events: mergeEvents(existingEvents, hydratedEvents),
    };
});
const initialState = {
    rooms: [],
    archivedRooms: [],
    users: {
        items: [],
        pagination: {},
    },
    publicRooms: [],
    eventRoom: {},
    selectedRoom: undefined,
    focusedEventId: undefined,
    state: 'loading',
};
const mergeEvents = (existingEvents, incomingEvents) => {
    const merged = [...existingEvents];
    incomingEvents.forEach((incoming) => {
        const existingIndex = merged.findIndex((event) => event.id === incoming.id || (event.txnId && event.txnId === incoming.txnId));
        if (existingIndex >= 0) {
            merged.splice(existingIndex, 1, preserveEventReadByMe(merged[existingIndex], incoming));
            return;
        }
        merged.push(incoming);
    });
    merged.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    return merged;
};
const preserveEventReadByMe = (existing, incoming) => {
    if (existing?.isReadByMe && !incoming.isReadByMe) {
        return { ...incoming, isReadByMe: true };
    }
    return incoming;
};
const mergeRoomReadState = (existing, next) => {
    if (!existing)
        return next;
    //mergeEvenrts is imp here because we want to merge existing and new events to keep older events loaded during searches.
    return {
        ...next,
        events: mergeEvents(existing.events, next.events),
    };
};
const getRoomFullfilled = (state, action) => {
    const nextRoom = action.payload?.room;
    if (!nextRoom) {
        state.state = 'ready';
        return;
    }
    const matrixRoom = mcli.getRoom(nextRoom.id);
    const isArchived = matrixRoom?.getMyMembership() === 'leave';
    const activeList = isArchived ? state.archivedRooms : state.rooms;
    const existingRoom = activeList.find((r) => r.id === nextRoom.id);
    const mergedRoom = mergeRoomReadState(existingRoom, nextRoom);
    mergedRoom.events.forEach((ev) => {
        state.eventRoom[ev.id] = ev.roomId;
    });
    // Ensure room doesn't exist in the opposite list.
    if (isArchived) {
        state.rooms = state.rooms.filter((r) => r.id !== nextRoom.id);
    }
    else {
        state.archivedRooms = state.archivedRooms.filter((r) => r.id !== nextRoom.id);
    }
    let isFound = false;
    const updatedList = activeList.map((r) => {
        if (r.id === nextRoom.id) {
            isFound = true;
            return mergedRoom;
        }
        return r;
    });
    if (!isFound) {
        updatedList.unshift(mergedRoom);
    }
    if (isArchived) {
        state.archivedRooms = updatedList;
    }
    else {
        state.rooms = updatedList;
    }
    state.state = 'ready';
};
export const matrixSlice = createSlice({
    name: 'matrix',
    initialState,
    reducers: {
        updateEvent: (state, action) => {
            const evt = action.payload.event;
            if (!evt) {
                // nothing to update
                return;
            }
            // Handle reaction events - trigger update for the target message
            if (evt.type === 'm.reaction') {
                const targetEventId = evt.relation?.event_id;
                if (targetEventId) {
                    const roomId = state.eventRoom[targetEventId];
                    if (roomId) {
                        // Find the target event and update its timestamp to trigger re-render
                        state.rooms = state.rooms.map((r) => {
                            if (r.id === roomId) {
                                r.events = r.events.map((ev) => {
                                    if (ev.id === targetEventId) {
                                        // Touch the event to trigger re-render (reactions are fetched from timeline)
                                        return { ...ev };
                                    }
                                    return ev;
                                });
                            }
                            return r;
                        });
                    }
                }
                return;
            }
            const roomId = action.payload?.roomId || state.eventRoom[evt.id];
            if (!roomId)
                return;
            // check if room exist, when not its a new room
            const newRoom = !state.rooms.some((r) => r.id === roomId);
            if (newRoom) {
                const _room = mcli.getRoom(roomId);
                if (!_room)
                    return;
                const roomIsDirect = isDirect(_room.roomId);
                const rawRoomName = _room?.name || '';
                const looksEmpty = !rawRoomName || rawRoomName.toLowerCase() === 'empty room' || rawRoomName.startsWith('!');
                // For DMs or 2-member rooms, resolve peer name instead of "Empty room"
                const roomMembers = _room
                    .getMembers()
                    .filter((m) => m.membership !== 'leave' && m.membership !== 'ban');
                const resolvedRoomName = looksEmpty && (roomIsDirect || roomMembers.length === 2)
                    ? getDirectPeerDisplayName(_room) || rawRoomName
                    : rawRoomName;
                state.rooms.push({
                    id: _room.roomId,
                    name: resolvedRoomName,
                    lastMessage: '',
                    lastEventId: '',
                    pin: false,
                    mute: false,
                    imageUrl: _room?.getAvatarUrl(mcli.baseUrl, 80, 80, 'crop') || '',
                    events: [evt],
                    members: [],
                    isPublicGroup: false,
                    userTyping: [],
                    isEncrypted: evt.type === 'm.room.encryption',
                    isDirect: roomIsDirect,
                });
            }
            state.eventRoom[evt.id] = roomId;
            state.rooms = state.rooms.map((r) => {
                if (r.id === roomId) {
                    if (evt.type === 'm.room.encryption')
                        r.isEncrypted = true;
                    // If this is a server-confirmed event, first remove any local echoes that match
                    const isServerConfirmed = evt.id.startsWith('$');
                    if (isServerConfirmed) {
                        r.events = r.events.filter((ev) => !(ev.id.startsWith('~local:') &&
                            ev.sender === evt.sender &&
                            (ev.content?.body || '') === (evt.content?.body || '') &&
                            Math.abs((ev.timestamp || 0) - (evt.timestamp || 0)) <= 5000));
                    }
                    const existingByTxn = evt.txnId
                        ? r.events.find((ev) => ev.txnId && ev.txnId === evt.txnId)
                        : undefined;
                    const existingByContentFallback = !existingByTxn && isServerConfirmed
                        ? r.events.find((ev) => ev.isSending &&
                            ev.sender === evt.sender &&
                            (ev.content?.body || '') === (evt.content?.body || '') &&
                            Math.abs((ev.timestamp || 0) - (evt.timestamp || 0)) <= 120000)
                        : undefined;
                    const replaceId = existingByTxn?.id || existingByContentFallback?.id;
                    const foundIndex = r.events.findIndex((ev) => ev.id === evt.id || (replaceId && ev.id === replaceId));
                    if (foundIndex >= 0) {
                        const existingEvent = r.events[foundIndex];
                        // Replace the existing event, forcing isSending to false for server-confirmed
                        if (isServerConfirmed) {
                            r.events[foundIndex] = preserveEventReadByMe(existingEvent, {
                                ...evt,
                                isSending: false,
                            });
                        }
                        else {
                            r.events[foundIndex] = preserveEventReadByMe(existingEvent, evt);
                        }
                    }
                    else {
                        // Not found, add it if server-confirmed or new
                        const toAdd = isServerConfirmed && evt.isSending ? { ...evt, isSending: false } : evt;
                        r.events.push(toAdd);
                    }
                    // Keep old eventRoom mapping so components can still find the event during transition
                    // The old ID (replaceId) will still point to the same room
                    // Sort events chronologically by timestamp to ensure date dividers are in correct order
                    r.events.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
                    r.lastMessage = r.events.at(-1)?.content?.body || '';
                }
                return r;
            });
        },
        leaveRoom: (state, action) => {
            const roomId = action.payload;
            const roomToArchive = state.rooms.find((room) => room.id === roomId);
            if (roomToArchive && !state.archivedRooms.some((room) => room.id === roomId)) {
                state.archivedRooms.push(roomToArchive);
            }
            state.rooms = state.rooms.filter((room) => room.id !== roomId);
            if (state.selectedRoom === roomId) {
                state.selectedRoom = state.rooms.at(0)?.id;
            }
        },
        removeRoom: (state, action) => {
            const roomId = action.payload;
            state.rooms = state.rooms.filter((room) => room.id !== roomId);
            state.archivedRooms = state.archivedRooms.filter((room) => room.id !== roomId);
            if (state.selectedRoom === roomId) {
                state.selectedRoom = state.rooms.at(0)?.id;
            }
        },
        updateMemberTyping: (state, action) => {
            const meId = mcli.getUserId();
            const member = action.payload;
            if (member.userId === meId)
                return;
            const found = state.rooms.find((r) => r.id === member.roomId);
            if (!found)
                return;
            if (member.typing) {
                const memberFound = found.userTyping.find((m) => m.userId === member.userId);
                if (!memberFound) {
                    found.userTyping.push(member);
                }
            }
            else {
                found.userTyping = found?.userTyping.filter((m) => m.userId !== member.userId);
            }
            state.rooms = state.rooms.map((r) => {
                if (found.id === r.id) {
                    return found;
                }
                return r;
            });
        },
        updateSelectedRoom: (state, action) => {
            const room = mcli.getRoom(action.payload);
            if (!room) {
                state.selectedRoom = '';
                return;
            }
            state.selectedRoom = action.payload;
            return state;
        },
        focusEvent: (state, action) => {
            state.focusedEventId = action.payload || undefined;
        },
        mergeRoomEvents: (state, action) => {
            const { roomId, events } = action.payload;
            if (!roomId || !Array.isArray(events) || events.length === 0)
                return;
            const updateRoomList = (rooms) => rooms.map((room) => {
                if (room.id !== roomId)
                    return room;
                const mergedEvents = mergeEvents(room.events, events);
                mergedEvents.forEach((event) => {
                    state.eventRoom[event.id] = event.roomId;
                });
                return { ...room, events: mergedEvents };
            });
            state.rooms = updateRoomList(state.rooms);
            state.archivedRooms = updateRoomList(state.archivedRooms);
        },
        clearUsers: (state) => {
            state.users = { items: [], pagination: {} };
        },
        refreshDirectFlags: (state) => {
            state.rooms = state.rooms.map((room) => ({
                ...room,
                isDirect: isDirect(room.id),
            }));
        },
        markEventsAsRead: (state, action) => {
            const { roomId, upToEventId } = action.payload;
            if (!roomId)
                return;
            state.rooms = state.rooms.map((r) => {
                if (r.id !== roomId)
                    return r;
                let foundTarget = false;
                const updatedEvents = r.events.map((ev) => {
                    if (ev.id === upToEventId) {
                        foundTarget = true;
                    }
                    // Mark all events up to and including the target as read
                    if (!foundTarget || ev.id === upToEventId) {
                        return { ...ev, isReadByMe: true };
                    }
                    return ev;
                });
                // If we didn't find the target, mark all events as read
                if (!foundTarget) {
                    return {
                        ...r,
                        events: r.events.map((ev) => ({ ...ev, isReadByMe: true })),
                    };
                }
                return { ...r, events: updatedEvents };
            });
        },
        updateRoomName: (state, action) => {
            const { roomId, name } = action.payload;
            if (!roomId || !name)
                return;
            state.rooms = state.rooms.map((r) => {
                if (r.id === roomId) {
                    return { ...r, name };
                }
                return r;
            });
        },
        clear: () => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getRoomsAsync.fulfilled, (state, action) => {
            if (!action.payload) {
                state.rooms = [];
                state.archivedRooms = [];
                state.eventRoom = {};
                state.state = 'ready';
                return;
            }
            state.rooms = action.payload.rooms;
            state.archivedRooms = action.payload.archivedRooms;
            state.eventRoom = action.payload.eventRoom;
            state.state = 'ready';
        });
        builder.addCase(getRoomsAsync.pending, (state) => {
            state.state = 'loading';
        });
        builder.addCase(getRoomsAsync.rejected, (state) => {
            state.state = 'error';
        });
        builder.addCase(paginateRoomAsync.fulfilled, getRoomFullfilled);
        builder.addCase(getUsersAsync.fulfilled, (state, action) => {
            state.users = action.payload.users;
            state.state = 'ready';
        });
        builder.addCase(jumpToRoomEventAsync.fulfilled, (state, action) => {
            const payload = action.payload;
            if (!payload)
                return;
            state.selectedRoom = payload.roomId;
            state.focusedEventId = payload.eventId;
            const updateRoomList = (rooms) => rooms.map((room) => {
                if (room.id !== payload.roomId)
                    return room;
                payload.events.forEach((event) => {
                    state.eventRoom[event.id] = event.roomId;
                });
                return { ...room, events: mergeEvents(room.events, payload.events) };
            });
            state.rooms = updateRoomList(state.rooms);
            state.archivedRooms = updateRoomList(state.archivedRooms);
        });
        builder.addCase(getPublicRooms.fulfilled, (state, action) => {
            state.publicRooms = action.payload.publicRooms;
            state.state = 'ready';
        });
        builder.addCase(getRoomAsync.fulfilled, getRoomFullfilled);
        builder.addCase(getRoomAsync.pending, (state) => {
            state.state = 'loading';
        });
        builder.addCase(getRoomAsync.rejected, (state) => {
            state.state = 'error';
        });
    },
});
export const { updateSelectedRoom, focusEvent, mergeRoomEvents, updateMemberTyping, updateEvent, clear, clearUsers, refreshDirectFlags, leaveRoom, removeRoom, markEventsAsRead, updateRoomName, } = matrixSlice.actions;
export const selectRooms = (state) => state.matrix.rooms;
export const selectArchivedRooms = (state) => state.matrix.archivedRooms;
export const selectPublicRooms = (state) => state.matrix.publicRooms;
// Memoized selector for room by ID - prevents unnecessary re-renders
const selectRoomById = createSelector([selectRooms, selectArchivedRooms, (_state, roomId) => roomId], (rooms, archivedRooms, roomId) => rooms.find((r) => r.id === roomId) || archivedRooms.find((r) => r.id === roomId));
export const selectRoom = (state, roomId) => selectRoomById(state, roomId);
// Memoized selector for room events - prevents array reference changes
export const selectRoomEvents = createSelector([selectRoomById], (room) => room?.events ?? []);
export const selectUsers = (state) => state.matrix.users;
export const selectState = (state) => state.matrix.state;
export const selectSelectedRoom = (state) => state.matrix.selectedRoom;
export const selectFocusedEventId = (state) => state.matrix.focusedEventId;
export const selectUserTypingByRoomId = (state, roomId) => {
    const f = state.matrix.rooms.find((r) => r.id === roomId) ||
        state.matrix.archivedRooms.find((r) => r.id === roomId);
    return f?.userTyping;
};
export const selectEvent = (state, eventId) => {
    const roomId = state.matrix.eventRoom[eventId];
    if (!roomId)
        return undefined;
    const room = state.matrix.rooms.find((r) => r.id === roomId) ||
        state.matrix.archivedRooms.find((r) => r.id === roomId);
    // First try by ID, then by txnId (for local events that got server ID)
    return room?.events.find((ev) => ev.id === eventId || ev.txnId === eventId.replace('~local:', ''));
};
export const selectMatrixEvent = (state, eventId) => {
    const roomId = state.matrix.eventRoom[eventId];
    if (!roomId)
        return undefined;
    const room = mcli.getRoom(roomId);
    const txnId = eventId.startsWith('~local:') ? eventId.replace('~local:', '') : undefined;
    return room
        ?.getLiveTimeline()
        .getEvents()
        .find((ev) => ev.getId() === eventId || (txnId && ev.getTxnId() === txnId));
};
export default matrixSlice.reducer;
